import React, { useState, useContext } from 'react';
import useSWR from 'swr';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Icon, Modal, Button } from 'antd';
import { config, Translater } from '../../../../config';
import { useHttp } from '../../../../hooks';
import * as Contexts from '../../../../contexts';
import { Wrapper, SelectedDaysGrid, DayBadge, CalendarWrapper } from './styled';
import moment from 'moment';
import { TDateInState, TDisabledDay } from './types';

type TProps = { restaurant: string; token: string | null };

export const DisabledBookingDays = ({ restaurant, token }: TProps) => {
  const { request, loading } = useHttp();
  const { language } = useContext(Contexts.LanguageContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState<TDateInState[]>([]);

  const swrKey = restaurant && token ? ['disabled-days', restaurant, token] : null;

  const { data, mutate } = useSWR<TDisabledDay[]>(
    swrKey,
    async (key, resId, authToken) => {
      return await request(
        `${config.API}/bookingDisabledDates?restaurant=${resId}&perPage=366`,
        'GET',
        null,
        { Authorization: `Bearer ${authToken}` },
      );
    },
    {
      onSuccess: (data) =>
        setSelectedDates(data?.map((item) => ({ date: item.date, _id: item._id }))),
    },
  );

  const handleDateChange = (date: Date | null) => {
    if (!date) return;
    const dateString = moment(date).format('YYYY-MM-DD');

    setSelectedDates((prev) => {
      const existing = prev.find((el) => el.date === dateString);
      if (existing) {
        return prev.filter((el) => el.date !== dateString);
      }
      return [...prev, { date: dateString }].sort((a, b) => a.date.localeCompare(b.date));
    });
  };

  const removeDate = (dateToRemove: TDateInState) => {
    setSelectedDates((prev) => prev.filter((date) => date !== dateToRemove));
  };

  const handleSave = async () => {
    if (!token || !data) return;

    const datesToDelete = data.filter(
      (original) => !selectedDates.some((current) => current.date === original.date),
    );

    const datesToAdd = selectedDates.filter((current) => !current._id).map((el) => el.date);

    if (datesToDelete.length === 0 && datesToAdd.length === 0) {
      setIsModalOpen(false);
      return;
    }

    try {
      const promises: Promise<any>[] = [];

      if (datesToDelete.length > 0) {
        promises.push(
          ...datesToDelete.map((item) =>
            fetch(`${config.API}/bookingDisabledDates/${item._id}`, {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }).then((res) => {
              if (!res.ok) {
                throw new Error('Delete failed');
              }
              return res;
            }),
          ),
        );
      }

      if (datesToAdd.length > 0) {
        promises.push(
          request(
            `${config.API}/bookingDisabledDates/bulk`,
            'POST',
            { dates: datesToAdd, restaurant },
            { Authorization: `Bearer ${token}` },
          ),
        );
      }

      await Promise.all(promises);
      await mutate();
      setIsModalOpen(false);
    } catch (e) {
      console.error('Помилка при синхронізації дат:', e);
    }
  };

  return (
    <Wrapper>
      <div className="main-controls">
        <Button type="primary" icon="calendar" onClick={() => setIsModalOpen(true)}>
          {Translater.disabledDays.title[language.slug]} ({data?.length})
        </Button>
      </div>

      <Modal
        title={Translater.disabledDays.title[language.slug]}
        visible={isModalOpen}
        onOk={handleSave}
        confirmLoading={loading}
        onCancel={() => setIsModalOpen(false)}
        width={800}
        okText={Translater.disabledDays.save[language.slug]}
        cancelText="Cancel"
      >
        <div className="content-layout">
          <CalendarWrapper>
            <DatePicker
              locale={language.slug === 'uk' ? 'uk' : 'en'}
              inline
              onChange={handleDateChange}
              highlightDates={selectedDates?.map((d) => new Date(d.date))}
              minDate={new Date()}
            />
          </CalendarWrapper>

          <SelectedDaysGrid>
            {selectedDates?.length === 0 && (
              <p className="empty-state">
                {Translater.disabledDays.selectDaysOnCalendar[language.slug]}
              </p>
            )}
            {selectedDates?.map((date) => (
              <DayBadge key={date.date}>
                <span>{date.date}</span>
                <Icon
                  type="close-circle"
                  theme="filled"
                  onClick={() => removeDate(date)}
                  style={{ cursor: 'pointer', color: '#ff4d4f' }}
                />
              </DayBadge>
            ))}
          </SelectedDaysGrid>
        </div>
      </Modal>
    </Wrapper>
  );
};
